/**
 * Runtime environment variable utility
 *
 * This utility allows React apps to read environment variables at runtime
 * instead of build time. Environment variables are injected via config.js
 * by the entrypoint.sh script in the Docker container.
 *
 * Usage:
 *   import { getEnv } from '@utils/env'
 *   const apiUrl = getEnv('REACT_APP_BUSINESS_LAYER_API')
 */

// Extend Window interface to include _env_
declare global {
  interface Window {
    _env_?: Record<string, string>
  }
}

/**
 * Get an environment variable value
 * First checks window._env_ (runtime), then falls back to process.env (build-time)
 *
 * @param key - The environment variable key (e.g., 'REACT_APP_BUSINESS_LAYER_API')
 * @param defaultValue - Optional default value if the variable is not found
 * @returns The environment variable value or defaultValue
 */
export const getEnv = (key: string, defaultValue: string = ''): string => {
  // Check runtime environment variables first (from config.js).
  // Use `in` so explicit empty strings (e.g. blanked AUTH domain for BFF) win over build-time.
  if (typeof window !== 'undefined' && window._env_ && key in window._env_) {
    return window._env_[key] ?? defaultValue
  }

  // Fallback to build-time environment variables
  const buildTimeValue = process.env[key]
  if (buildTimeValue) {
    return buildTimeValue
  }

  return defaultValue
}

/**
 * Get all environment variables
 * Merges runtime (window._env_) and build-time (process.env) variables
 * Runtime variables take precedence
 *
 * @returns Object containing all environment variables
 */
export const getAllEnv = (): Record<string, string> => {
  const runtimeEnv =
    typeof window !== 'undefined' && window._env_ ? window._env_ : {}
  const buildTimeEnv = process.env

  // Convert process.env to Record<string, string> by filtering out undefined values
  const buildTimeEnvFiltered: Record<string, string> = {}
  Object.keys(buildTimeEnv).forEach((key) => {
    const value = buildTimeEnv[key]
    if (value !== undefined) {
      buildTimeEnvFiltered[key] = value
    }
  })

  return {
    ...buildTimeEnvFiltered,
    ...runtimeEnv, // Runtime env overrides build-time env
  }
}
